import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import Modal from "./Modal";
import Alert from "./Alert";
import alertsService from "../../services/alertsService";
import MessagingService from "../../services/core/MessagingService";

const DEFAULT_CRON = "0 0 8 * * *";

// A superuser's recipient list mixes users/groups from every tenant - without
// this, e.g. several unrelated "Todos" groups are indistinguishable. Only
// label with the tenant when more than one is actually present, so a normal
// (single-tenant) user doesn't see redundant noise on every row.
const hasMultipleTenants = (items) => new Set((items || []).map((i) => i.tenantId).filter(Boolean)).size > 1;
const userLabel = (u, showTenant) =>
  `${u.displayName || u.email}${showTenant && u.tenantName ? ` · ${u.tenantName}` : ""}`;
const groupLabel = (g, showTenant) =>
  `${g.name} (${g.memberCount})${showTenant && g.tenantName ? ` · ${g.tenantName}` : ""}`;

const SaveFilterAsAlertModal = ({ isOpen, onClose, entityName, filters, columns, columnLabels, onCreated }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [cron, setCron] = useState(DEFAULT_CRON);
  const [channelEmail, setChannelEmail] = useState(true);
  const [channelMessage, setChannelMessage] = useState(false);
  const [extraEmails, setExtraEmails] = useState("");
  const [recipientUserIds, setRecipientUserIds] = useState([]);
  const [recipientGroupIds, setRecipientGroupIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setName("");
    setSubject("");
    setCron(DEFAULT_CRON);
    setChannelEmail(true);
    setChannelMessage(false);
    setExtraEmails("");
    setRecipientUserIds([]);
    setRecipientGroupIds([]);
    setError("");
    Promise.all([MessagingService.users(), MessagingService.groups()])
      .then(([userRows, groupRows]) => {
        setUsers((userRows || []).filter((u) => u.messagingEnabled !== false));
        setGroups(groupRows || []);
      })
      .catch(() => {
        setUsers([]);
        setGroups([]);
      });
  }, [isOpen]);

  const toggleUserId = (userId) =>
    setRecipientUserIds((current) =>
      current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId],
    );

  const toggleGroupId = (groupId) =>
    setRecipientGroupIds((current) =>
      current.includes(groupId) ? current.filter((id) => id !== groupId) : [...current, groupId],
    );

  const submit = async () => {
    if (!name.trim() || !subject.trim() || !cron.trim()) return;
    setSaving(true);
    setError("");
    try {
      await alertsService.createSavedFilterAlert({
        entityKey: entityName,
        name: name.trim(),
        subject: subject.trim(),
        filters: JSON.stringify(filters || {}),
        columns: columns || [],
        columnLabels: columnLabels || [],
        cron: cron.trim(),
        channelEmail,
        channelMessage,
        recipientUserIds,
        recipientGroupIds,
        recipientEmails: extraEmails
          .split(/[,;]/)
          .map((v) => v.trim())
          .filter(Boolean),
      });
      onCreated?.();
      onClose();
    } catch (e) {
      setError(e?.message || t("common.errorOccurred"));
    } finally {
      setSaving(false);
    }
  };

  const filterCount = Object.keys(filters || {}).length;
  const showUserTenant = useMemo(() => hasMultipleTenants(users), [users]);
  const showGroupTenant = useMemo(() => hasMultipleTenants(groups), [groups]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("alerts.saveAsAlert.title")} icon={faBell}>
      {error && <Alert type="error" message={error} onClose={() => setError("")} />}
      <p>{t("alerts.saveAsAlert.subtitle", { count: filterCount })}</p>
      <p className="alert-approval-hint">
        {(columns || []).length > 0
          ? t("alerts.saveAsAlert.columnsPreview", {
              count: columns.length,
              list: (columnLabels && columnLabels.length === columns.length ? columnLabels : columns).join(", "),
            })
          : t("alerts.saveAsAlert.columnsPreviewEmpty")}
      </p>
      <div className="form-group">
        <label htmlFor="saved-filter-alert-name">{t("alerts.saveAsAlert.name")}</label>
        <input
          id="saved-filter-alert-name"
          className="form-control"
          value={name}
          maxLength={160}
          autoFocus
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="form-group">
        <label htmlFor="saved-filter-alert-subject">{t("alerts.saveAsAlert.subject")}</label>
        <input
          id="saved-filter-alert-subject"
          className="form-control"
          value={subject}
          maxLength={255}
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>
      <div className="form-group">
        <label htmlFor="saved-filter-alert-cron">{t("alerts.fields.cron")}</label>
        <input
          id="saved-filter-alert-cron"
          className="form-control"
          value={cron}
          onChange={(e) => setCron(e.target.value)}
        />
        <small>{t("alerts.fields.cronHint")}</small>
      </div>
      <div className="alert-channels">
        <label>
          <input type="checkbox" checked={channelEmail} onChange={(e) => setChannelEmail(e.target.checked)} />
          {t("alerts.fields.channelEmail")}
        </label>
        <label>
          <input type="checkbox" checked={channelMessage} onChange={(e) => setChannelMessage(e.target.checked)} />
          {t("alerts.fields.channelMessage")}
        </label>
      </div>
      <div className="form-group">
        <label htmlFor="saved-filter-alert-emails">{t("alerts.fields.extraEmails")}</label>
        <input
          id="saved-filter-alert-emails"
          className="form-control"
          placeholder="correo1@ejemplo.com, correo2@ejemplo.com"
          value={extraEmails}
          onChange={(e) => setExtraEmails(e.target.value)}
        />
      </div>
      <div className="alert-recipients">
        <div className="alert-recipients-column">
          <strong>{t("alerts.fields.recipientUsers")}</strong>
          <div className="alert-recipients-list">
            {users.map((u) => (
              <label key={u.userId}>
                <input
                  type="checkbox"
                  checked={recipientUserIds.includes(u.userId)}
                  onChange={() => toggleUserId(u.userId)}
                />
                <span className="alert-recipient-label-text">{userLabel(u, showUserTenant)}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="alert-recipients-column">
          <strong>{t("alerts.fields.recipientGroups")}</strong>
          <div className="alert-recipients-list">
            {groups.map((g) => (
              <label key={g.id}>
                <input
                  type="checkbox"
                  checked={recipientGroupIds.includes(g.id)}
                  onChange={() => toggleGroupId(g.id)}
                />
                <span className="alert-recipient-label-text">{groupLabel(g, showGroupTenant)}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
      <p className="alert-approval-hint">{t("alerts.saveAsAlert.approvalHint")}</p>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          {t("common.cancel")}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={saving || !name.trim() || !subject.trim() || !cron.trim()}
          onClick={submit}
        >
          {t("common.save")}
        </button>
      </div>
    </Modal>
  );
};

export default SaveFilterAsAlertModal;
